(() => {
  const EXTERNAL_SUFFIX = 'Redirecting to a third-party website (opens in a new tab)';

  const normalizeText = (text) => (text || '').replace(/\s+/g, ' ').trim();

  // Best available accessible name for a link: existing label, visible text, then image alt / title
  const getLinkName = (link) => {
    const img = link.querySelector('img[alt]');
    return normalizeText(
      link.getAttribute('aria-label') ||
      link.textContent ||
      (img && img.getAttribute('alt')) ||
      link.getAttribute('title')
    );
  };

  const stripWww = (host) => host.replace(/^www\./, '');

  const isThirdParty = (href) => {
    try {
      const url = new URL(href, window.location.href);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
      return stripWww(url.hostname) !== stripWww(window.location.hostname);
    } catch (e) {
      return false;
    }
  };

  // External Links Warning
  const handleExternalLinks = () => {
    document.querySelectorAll('a[href]:not([data-a11y-external])').forEach(link => {
      if (!isThirdParty(link.getAttribute('href'))) return;

      const name = getLinkName(link);
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.setAttribute('aria-label', name ? `${name}, ${EXTERNAL_SUFFIX}` : EXTERNAL_SUFFIX);
      link.setAttribute('data-a11y-external', '');
    });
  };

  // Email & Phone Links
  const handleContactLinks = () => {
    document.querySelectorAll('a[href^="mailto:"]:not([data-a11y-contact]), a[href^="tel:"]:not([data-a11y-contact])').forEach(link => {
      const href = link.getAttribute('href');
      const isEmail = href.startsWith('mailto:');
      // Fall back to the address itself when the link has no text (e.g. icon-only links)
      const value = normalizeText(link.textContent) || decodeURIComponent(href.replace(/^(mailto|tel):/, '').split('?')[0]);

      link.setAttribute('aria-label', `${isEmail ? 'Email Address' : 'Phone number'}: ${value}`);
      link.setAttribute('data-a11y-contact', '');
    });
  };

  // Remove unwanted aria-labelledby
  const removeAriaLabelledby = () => {
    document.querySelectorAll('.card__heading a[aria-labelledby], .card__heading h3[aria-labelledby]')
      .forEach(el => el.removeAttribute('aria-labelledby'));
  };

  // Remove aria-describedby
  const removeAriaDescribedby = () => {
    document.querySelectorAll('.thumbnail.global-media-settings.global-media-settings--no-shadow[aria-describedby], .yotpo-widget-clear[aria-describedby]')
      .forEach(el => el.removeAttribute('aria-describedby'));
  };

  // Add aria-hidden to <br>
  const hideBrFromScreenReaders = () => {
    document.querySelectorAll('br:not([aria-hidden])').forEach(br => br.setAttribute('aria-hidden', 'true'));
  };

  // Remove tabindex from mega menu
  const fixMegaMenuTabindex = () => {
    document.querySelectorAll('.js .mega-menu__content[tabindex]').forEach(el => el.removeAttribute('tabindex'));
  };

  // Hide slideshow navigation when there is only one slide
  const handleSlideshowNav = () => {
    document.querySelectorAll('slideshow-component').forEach(slider => {
      const nav = slider.querySelector('.slideshow-nav');
      if (!nav) return;

      if (slider.querySelectorAll('.slideshow__slide').length <= 1) {
        nav.style.display = 'none';
        nav.setAttribute('aria-hidden', 'true');
      } else {
        nav.style.display = '';
        nav.removeAttribute('aria-hidden');
      }
    });
  };

  // Adding accessible label for banner buttons
  const labelBannerButtons = () => {
    document.querySelectorAll('.banner__box').forEach(banner => {
      const heading = banner.querySelector('h2.banner__heading');
      const headingText = heading && normalizeText(heading.textContent);
      if (!headingText) return;

      banner.querySelectorAll('a.button').forEach(button => {
        const buttonText = normalizeText(button.textContent);
        if (buttonText) button.setAttribute('aria-label', `${buttonText}, ${headingText}`);
      });
    });
  };

  // Tracking pixels injected by third-party scripts (e.g. Lantern) have no alt; mark them decorative
  const hideTrackingPixels = () => {
    document.querySelectorAll('img:not([alt])').forEach(img => {
      const isPixel = ['0', '1'].includes(img.getAttribute('width')) && ['0', '1'].includes(img.getAttribute('height'));
      const isTracker = /lantern\.roeye\.com/.test(img.getAttribute('src') || '');
      if (!isPixel && !isTracker) return;

      img.setAttribute('alt', '');
      img.setAttribute('aria-hidden', 'true');
    });
  };

  // <summary> is natively a button; the role=button added by global.js, details-modal.js and
  // facets.js makes its children presentational (hiding accordion headings and links)
  const fixSummaryRoles = () => {
    document.querySelectorAll('details > summary').forEach(summary => {
      if (summary.getAttribute('role') === 'button') summary.removeAttribute('role');
      if (summary.hasAttribute('data-a11y-summary')) return;
      summary.setAttribute('data-a11y-summary', '');

      const details = summary.parentElement;

      // Keep aria-expanded true to the real state, including menus opened on hover
      details.addEventListener('toggle', () => {
        if (summary.hasAttribute('aria-expanded')) summary.setAttribute('aria-expanded', details.open);
      });

      // Desktop mega menu opens on hover; a mouse click on its title used to land on a
      // link inside the summary, so keep the menu open instead of toggling it closed
      if (summary.closest('hover-details-disclosure')) {
        summary.addEventListener('click', event => {
          if (event.detail > 0 && details.open) {
            event.preventDefault();
            summary.setAttribute('aria-expanded', 'true');
          }
        });
      }
    });
  };

  // Scrolling text (marquee section) with a pause/play control (WCAG 2.2.2).
  // Same motion as marquee.js: two copies appended, 1px per frame, loop after one text width.
  const initMarquees = () => {
    document.querySelectorAll('[data-marquee]:not([data-a11y-marquee])').forEach(track => {
      track.setAttribute('data-a11y-marquee', '');
      const first = track.firstElementChild;
      if (!first) return;

      // Copies are visual only, so screen readers read the text once, as a normal heading
      for (let n = 0; n < 2; n++) {
        const copy = first.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('id');
        copy.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        track.appendChild(copy);
      }

      const toggle = track.parentElement.querySelector('[data-marquee-toggle]');
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let offset = 0;
      let frame = null;

      const step = () => {
        first.style.marginLeft = `-${offset}px`;
        if (offset > first.clientWidth) offset = 0;
        offset += 1;
        frame = requestAnimationFrame(step);
      };

      const setPlaying = playing => {
        if (playing && frame === null) frame = requestAnimationFrame(step);
        if (!playing && frame !== null) {
          cancelAnimationFrame(frame);
          frame = null;
        }
        if (!toggle) return;
        toggle.classList.toggle('is-paused', !playing);
        toggle.setAttribute('aria-label', toggle.dataset[playing ? 'labelPause' : 'labelPlay']);
      };

      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => setPlaying(frame === null));
      }

      // Respect the reduced-motion preference: start paused, user can still press play
      setPlaying(!reduceMotion);
    });
  };

  // Slideshow: global.js removes tabindex from every link in the visible slide, which puts the
  // aria-hidden full-slide overlay link back in the tab order; keep it out
  const guardHiddenSlideLinks = () => {
    document.querySelectorAll('.banner__link[aria-hidden="true"]:not([tabindex="-1"])')
      .forEach(link => link.setAttribute('tabindex', '-1'));
  };

  // role=list / role=listitem are redundant on ul/ol/li. Safari drops list semantics when
  // list-style is none, so those lists get an invisible marker instead of the role
  const fixListRoles = () => {
    const keepSemantics = el => {
      const style = getComputedStyle(el);
      if (style.listStyleType === 'none' && style.listStylePosition === 'outside') {
        el.style.listStyleType = '"\\200B"';
      }
    };

    document.querySelectorAll('ul[role="list"], ol[role="list"]').forEach(list => {
      list.removeAttribute('role');
      keepSemantics(list);
      Array.from(list.children).forEach(item => item.tagName === 'LI' && keepSemantics(item));
    });
    document.querySelectorAll('ul > li[role="listitem"], ol > li[role="listitem"]')
      .forEach(item => item.removeAttribute('role'));
  };

  // Third-party widgets (Yotpo, Rebuy): attribute-only fixes, no markup is moved or replaced
  const fixWidgetSemantics = () => {
    // Yotpo tile groups use fieldset only for layout
    document.querySelectorAll('fieldset.yotpo-tile-group').forEach(fieldset => {
      if (fieldset.querySelector('input, select, textarea, button, a[href]')) return;
      fieldset.setAttribute('role', 'presentation');
      fieldset.removeAttribute('aria-label');
    });

    // Yotpo campaign tiles: aria-label on a plain div is ignored, so give it a role that can be named
    document.querySelectorAll('div[class*="yotpo-campaign"][aria-label]:not([role])').forEach(tile => {
      const hasControls = tile.querySelector('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      tile.setAttribute('role', tile.tabIndex >= 0 && !hasControls ? 'button' : 'group');
    });

    // Yotpo review filter buttons: the visible text ("5 1") must be part of the name
    document.querySelectorAll('button[aria-pressed][aria-label*=" star by "]').forEach(button => {
      const visible = normalizeText(button.textContent);
      const label = button.getAttribute('aria-label');
      if (visible && !label.startsWith(visible)) button.setAttribute('aria-label', `${visible}, ${label}`);
    });

    // Rebuy bundle: role=list containers that also hold buttons are not real lists
    document.querySelectorAll('[class*="rebuy"][role="list"]').forEach(list => {
      const children = Array.from(list.children);
      if (children.every(child => child.getAttribute('role') === 'listitem')) return;
      list.removeAttribute('role');
      children.forEach(child => child.getAttribute('role') === 'listitem' && child.removeAttribute('role'));
    });

    // Rebuy bundle: headings inside role=button are ignored anyway; the button keeps the text as its name
    document.querySelectorAll('[class*="rebuy"] [role="button"] :is(h1, h2, h3, h4, h5, h6):not([role])')
      .forEach(heading => heading.setAttribute('role', 'none'));
  };

  // Nearest ancestor (or self) that paints a background
  const backgroundOwner = el => {
    for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
      const bg = getComputedStyle(node).backgroundColor;
      if (bg && bg !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(bg)) return { node, bg };
    }
    return { node: document.body, bg: 'rgb(255, 255, 255)' };
  };

  // Third-party widget colors that fail WCAG AA contrast; the closest passing color is used
  const WIDGET_CONTRAST = [
    // Rebuy "Add To Bundle": white on #2491C4 (3.55:1) -> darker blue #1D76A1 (5.05:1)
    { scope: '[class*="rebuy"]', color: 'rgb(255, 255, 255)', bg: 'rgb(36, 145, 196)', set: 'background', value: '#1D76A1' },
    // Yotpo rewards banner: white on #D88F88 (2.56:1) -> theme text #121212 (7.32:1)
    { scope: '[class*="yotpo"]', color: 'rgb(255, 255, 255)', bg: 'rgb(216, 143, 136)', set: 'color', value: '#121212' },
    // Yotpo redemption text: #6C6C6C on #F2E8DA (4.33:1) -> #636363 (4.96:1)
    { scope: '[class*="yotpo"]', color: 'rgb(108, 108, 108)', bg: 'rgb(242, 232, 218)', set: 'color', value: '#636363' },
  ];

  const fixWidgetContrast = () => {
    WIDGET_CONTRAST.forEach(rule => {
      document.querySelectorAll(`${rule.scope}, ${rule.scope} *`).forEach(el => {
        const hasText = Array.from(el.childNodes).some(n => n.nodeType === 3 && n.textContent.trim());
        if (!hasText || getComputedStyle(el).color !== rule.color) return;
        const owner = backgroundOwner(el);
        if (owner.bg !== rule.bg) return;
        if (rule.set === 'background') owner.node.style.setProperty('background-color', rule.value, 'important');
        else el.style.setProperty('color', rule.value, 'important');
      });
    });

    // Rebuy "Top" button text only had the browser default color next to an author background
    document.querySelectorAll('[class*="rebuy"] span').forEach(span => {
      if (normalizeText(span.textContent) !== 'Top' || span.style.color) return;
      span.style.setProperty('color', getComputedStyle(span).color);
    });
  };

  // All fixes are idempotent, so they can safely re-run when content is injected later
  const runFixes = () => {
    guardHiddenSlideLinks();
    fixListRoles();
    fixWidgetSemantics();
    fixWidgetContrast();
    initMarquees();
    fixSummaryRoles();
    hideTrackingPixels();
    handleExternalLinks();
    handleContactLinks();
    removeAriaLabelledby();
    removeAriaDescribedby();
    hideBrFromScreenReaders();
    fixMegaMenuTabindex();
    handleSlideshowNav();
    labelBannerButtons();
  };

  const init = () => {
    runFixes();

    // Re-apply fixes to content added after load (apps like Yotpo, section rendering, cart drawer, etc.)
    let scheduled = null;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = setTimeout(() => {
        scheduled = null;
        runFixes();
      }, 250);
    }).observe(document.body, { childList: true, subtree: true });

    // The slideshow changes tabindex without adding nodes, so watch that attribute separately
    new MutationObserver(guardHiddenSlideLinks)
      .observe(document.body, { attributes: true, attributeFilter: ['tabindex'], subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
